/**
 * usePreferences Hook
 * Manages UI preferences with server persistence and localStorage migration
 *
 * Usage:
 *   const { preferences, updatePreference, isLoading } = usePreferences();
 *   // Access: preferences.tradingPanel?.position
 *   // Update: updatePreference('tradingPanel', { position: { x: 100, y: 200 } });
 */

import { useState, useEffect, useCallback } from 'react';
import { CONFIG } from '../config/ports';
import { apiFetch } from '../services/api';

const API_BASE = `${CONFIG.getBackendUrl()}/api/user`;

// Debounce delay for saving preferences (ms)
const SAVE_DEBOUNCE = 500;

export interface UIPreferences {
  tradingPanel?: {
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    // Mobile-specific layout (separate from desktop)
    mobilePosition?: { x: number; y: number };
    mobileSize?: { width: number; height: number };
    // Visibility
    visible?: boolean;
    // Shared settings (apply to both mobile and desktop)
    /** The working order size the user currently has dialled in. */
    quantity?: number;
    /**
     * The user's default order size: what a fresh ticket starts at and what
     * `quantity` falls back to when a position closes. Read/written only via
     * useDefaultQuantity so desktop and mobile cannot drift.
     */
    defaultQuantity?: number;
    useOCO?: boolean;
    ocoTPEnabled?: boolean;
    ocoSLEnabled?: boolean;
    ocoTPOffset?: number;
    ocoSLOffset?: number;
  };
  predictionPanel?: {
    position?: { x: number; y: number };
  };
  pendingOrdersPanel?: {
    position?: { x: number; y: number };
  };
  atrDisplay?: {
    position?: { x: number; y: number };
  };
  liveTradingOverlay?: {
    position?: { x: number; y: number };
    isMinimized?: boolean;
    // Mobile-specific layout
    mobilePosition?: { x: number; y: number };
    mobileIsMinimized?: boolean;
  };
  positionControlsOverlay?: {
    position?: { x: number; y: number };
    isMinimized?: boolean;
    // Mobile-specific layout
    mobilePosition?: { x: number; y: number };
    mobileIsMinimized?: boolean;
  };
  backtestControls?: {
    position?: { x: number; y: number };
  };
  chart?: {
    showOrderMarkers?: boolean;
    visibleRange?: { from: number; to: number; scrollPosition?: number; durationMs?: number };
    priceRange?: {
      min: number;
      max: number;
      autoScale: boolean;
    };
    volumePaneHeight?: number; // Height in pixels for the volume pane
  };
  signalPredictor?: {
    isMinimized?: boolean;
    showCandle?: boolean;
    showStats?: boolean;
    position?: { x: number; y: number };
    // Mobile-specific layout
    mobilePosition?: { x: number; y: number };
    mobileIsMinimized?: boolean;
    settings?: {
      enabled?: boolean;
      theme?: string;
      opacity?: number;
      ntfyEnabled?: boolean;
      ntfyTopic?: string;
    };
    stats?: {
      totalAlerts?: number;
      signals?: number;
      skipped?: number;
      falsePositives?: number;
    };
  };
  theme?: string;
  starredThemes?: string[];
  liveLogVisible?: boolean;
  overlayLibrary?: {
    visibleOverlays?: string[];
  };
  /**
   * Hand labels for the setup-candidate research tool, keyed by
   * "{symbol}:{timestamp}". Free-form on the backend (preferences are stored
   * as an opaque JSON blob), so this grows without a schema migration.
   */
  setupLabels?: Record<string, { verdict: string; reason: string; at: number }>;
  newsPanel?: {
    position?: { x: number; y: number };
    mobilePosition?: { x: number; y: number };
    visible?: boolean;
    activeTab?: 'news' | 'calendar';
    calendarFilters?: {
      timeRange?: 'tomorrow' | 'week';
      countries?: string[];
      importance?: ('high' | 'medium' | 'low')[];
    };
  };
  // Backtest state persistence
  backtestSeekbarIndex?: number;
  backtestPlaybackState?: 'playing' | 'paused' | 'stopped' | 'completed';
  // Action Bar Positions
  actionBarPositions?: {
    isPinned?: boolean;
  };
  // Action Bar Health visibility and pin state
  actionBarHealth?: {
    visible?: boolean;
    isPinned?: boolean;
  };
  // Alert Button pin state
  alertButton?: {
    isPinned?: boolean;
  };
  updatedAt?: number;
}

// Singleton state to share across hook instances
let globalPreferences: UIPreferences = {};
let globalLoading = true;
let globalInitialized = false;
let initializationPromise: Promise<void> | null = null; // Track ongoing initialization
const listeners = new Set<(prefs: UIPreferences) => void>();

// Collect localStorage keys to migrate
const LOCAL_STORAGE_KEYS_TO_MIGRATE = [
  'tradingPanelPosition',
  'tradingPanelSize',
  'tradingPanelQuantity',
  'ocoFormVisible',
  'ocoTPOffset',
  'ocoSLOffset',
  'predictionPanelPosition',
  'pendingOrdersPosition',
  'atrDisplayPosition',
  'liveTradingOverlayState',
  'positionControlsOverlayState',
  'showOrderMarkers',
  'chartVisibleRange',
  'signalPredictorSettings',
  'signalPredictorStats',
  'signalPredictorMinimized',
  'signalPredictorShowCandle',
  'signalPredictorShowStats',
  'signalPredictorPosition',
  'selectedTheme',
  'liveLogVisible',
];

/**
 * Collect data from localStorage and format for migration
 */
function collectLocalStorageData(): UIPreferences {
  const prefs: UIPreferences = {};

  // Trading Panel
  const tradingPos = localStorage.getItem('tradingPanelPosition');
  const tradingSize = localStorage.getItem('tradingPanelSize');
  const tradingQty = localStorage.getItem('tradingPanelQuantity');
  const useOCO = localStorage.getItem('ocoFormVisible');
  const ocoTP = localStorage.getItem('ocoTPOffset');
  const ocoSL = localStorage.getItem('ocoSLOffset');

  if (tradingPos || tradingSize || tradingQty || useOCO || ocoTP || ocoSL) {
    prefs.tradingPanel = {
      position: tradingPos ? JSON.parse(tradingPos) : undefined,
      size: tradingSize ? JSON.parse(tradingSize) : undefined,
      quantity: tradingQty ? parseInt(tradingQty) : undefined,
      useOCO: useOCO ? JSON.parse(useOCO) : undefined,
      ocoTPOffset: ocoTP ? parseInt(ocoTP) : undefined,
      ocoSLOffset: ocoSL ? parseInt(ocoSL) : undefined,
    };
  }

  // Prediction Panel
  const predPos = localStorage.getItem('predictionPanelPosition');
  if (predPos) {
    prefs.predictionPanel = { position: JSON.parse(predPos) };
  }

  // Pending Orders Panel
  const pendingPos = localStorage.getItem('pendingOrdersPosition');
  if (pendingPos) {
    prefs.pendingOrdersPanel = { position: JSON.parse(pendingPos) };
  }

  // ATR Display
  const atrPos = localStorage.getItem('atrDisplayPosition');
  if (atrPos) {
    prefs.atrDisplay = { position: JSON.parse(atrPos) };
  }

  // Live Trading Overlay
  const liveState = localStorage.getItem('liveTradingOverlayState');
  if (liveState) {
    prefs.liveTradingOverlay = JSON.parse(liveState);
  }

  // Position Controls Overlay
  const posCtrlState = localStorage.getItem('positionControlsOverlayState');
  if (posCtrlState) {
    prefs.positionControlsOverlay = JSON.parse(posCtrlState);
  }

  // Chart
  const showMarkers = localStorage.getItem('showOrderMarkers');
  const visRange = localStorage.getItem('chartVisibleRange');
  if (showMarkers || visRange) {
    prefs.chart = {
      showOrderMarkers: showMarkers ? JSON.parse(showMarkers) : undefined,
      visibleRange: visRange ? JSON.parse(visRange) : undefined,
    };
  }

  // Signal Predictor
  const spSettings = localStorage.getItem('signalPredictorSettings');
  const spStats = localStorage.getItem('signalPredictorStats');
  const spMin = localStorage.getItem('signalPredictorMinimized');
  const spCandle = localStorage.getItem('signalPredictorShowCandle');
  const spStatsShow = localStorage.getItem('signalPredictorShowStats');
  const spPos = localStorage.getItem('signalPredictorPosition');
  if (spSettings || spStats || spMin || spCandle || spStatsShow || spPos) {
    prefs.signalPredictor = {
      settings: spSettings ? JSON.parse(spSettings) : undefined,
      stats: spStats ? JSON.parse(spStats) : undefined,
      isMinimized: spMin === 'true',
      showCandle: spCandle !== 'false',
      showStats: spStatsShow === 'true',
      position: spPos ? JSON.parse(spPos) : undefined,
    };
  }

  // Theme
  const theme = localStorage.getItem('selectedTheme');
  if (theme) {
    prefs.theme = theme;
  }

  // Live Log
  const logVis = localStorage.getItem('liveLogVisible');
  if (logVis) {
    prefs.liveLogVisible = logVis === 'true';
  }

  return prefs;
}

/**
 * Clear migrated localStorage keys
 */
function clearMigratedLocalStorage(): void {
  LOCAL_STORAGE_KEYS_TO_MIGRATE.forEach(key => {
    localStorage.removeItem(key);
  });
  // Mark migration as complete
  localStorage.setItem('preferences_migrated', 'true');
}

/**
 * Initialize preferences: load from server, migrate from localStorage if needed
 * Uses a shared promise to ensure concurrent calls wait for the same initialization
 */
async function initializePreferences(): Promise<void> {
  // If already completed, return immediately
  if (globalInitialized && !globalLoading) return;

  // If initialization is in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization and store the promise
  initializationPromise = doInitializePreferences();
  return initializationPromise;
}

/**
 * Actual initialization logic (called only once)
 */
async function doInitializePreferences(): Promise<void> {
  globalInitialized = true;

  try {
    // Check if we already migrated
    const migrated = localStorage.getItem('preferences_migrated') === 'true';

    if (!migrated) {
      // Collect localStorage data
      const localData = collectLocalStorageData();

      if (Object.keys(localData).length > 0) {
        // Try to migrate to server
        const response = await apiFetch(`${API_BASE}/preferences/migrate`, {
          method: 'POST',
          body: JSON.stringify(localData),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.migrated) {
            console.log('[PREFERENCES] Migrated localStorage to server');
            clearMigratedLocalStorage();
          }
          globalPreferences = result.preferences || localData;
        } else {
          // Server error, use local data
          globalPreferences = localData;
        }
      }
    }

    // Load from server (either migrated or existing)
    console.log('[PREFERENCES] Fetching from server...');
    const response = await apiFetch(`${API_BASE}/preferences`);
    console.log('[PREFERENCES] Response status:', response.status, response.ok);
    if (response.ok) {
      const data = await response.json();
      console.log('[PREFERENCES] Raw response:', JSON.stringify(data));
      console.log('[PREFERENCES] signalPredictor.isMinimized:', data?.signalPredictor?.isMinimized);
      globalPreferences = data;
    } else {
      console.log('[PREFERENCES] Response not OK:', await response.text());
    }
  } catch (error) {
    console.error('[PREFERENCES] Error initializing:', error);
    // Fallback to localStorage data if server unavailable
    globalPreferences = collectLocalStorageData();
  }

  globalLoading = false;
  console.log('[PREFERENCES] Loading complete, notifying listeners');
  listeners.forEach(fn => fn(globalPreferences));
}

// Save queue for debouncing
let saveTimeout: NodeJS.Timeout | null = null;
let pendingUpdates: Partial<UIPreferences> = {};

/**
 * Save preferences to server (debounced)
 */
async function savePreferences(updates: Partial<UIPreferences>): Promise<void> {
  // Merge into pending updates
  pendingUpdates = deepMerge(pendingUpdates, updates);

  // Clear existing timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  // Debounce save
  saveTimeout = setTimeout(async () => {
    const toSave = { ...pendingUpdates };
    pendingUpdates = {};

    try {
      const response = await apiFetch(`${API_BASE}/preferences`, {
        method: 'PATCH',
        body: JSON.stringify(toSave),
      });

      if (response.ok) {
        const updated = await response.json();
        globalPreferences = updated;
        listeners.forEach(fn => fn(globalPreferences));
      }
    } catch (error) {
      console.error('[PREFERENCES] Error saving:', error);
    }
  }, SAVE_DEBOUNCE);
}

/**
 * Deep merge helper
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      (result as any)[key] = deepMerge(targetValue, sourceValue);
    } else if (sourceValue !== undefined) {
      (result as any)[key] = sourceValue;
    }
  }

  return result;
}

/**
 * React hook for accessing and updating UI preferences
 */
export function usePreferences() {
  const [preferences, setPreferences] = useState<UIPreferences>(globalPreferences);
  const [isLoading, setIsLoading] = useState(globalLoading);

  useEffect(() => {
    // Initialize on first mount
    initializePreferences();

    // Subscribe to updates
    const updateHandler = (prefs: UIPreferences) => {
      setPreferences(prefs);
      setIsLoading(false);
    };

    listeners.add(updateHandler);
    return () => {
      listeners.delete(updateHandler);
    };
  }, []);

  /**
   * Update a specific preference key
   */
  const updatePreference = useCallback(
    <K extends keyof UIPreferences>(
      key: K,
      value: Partial<UIPreferences[K]>
    ) => {
      // Optimistic update
      const current = globalPreferences[key] || {};
      const updated = (typeof value === 'object' && value !== null && !Array.isArray(value))
        ? { ...current, ...value }
        : value;

      globalPreferences = {
        ...globalPreferences,
        [key]: updated,
      };

      // Notify listeners
      listeners.forEach(fn => fn(globalPreferences));

      // Save to server
      savePreferences({ [key]: updated } as Partial<UIPreferences>);
    },
    []
  );

  /**
   * Set full preferences object
   */
  const setFullPreferences = useCallback((prefs: UIPreferences) => {
    globalPreferences = prefs;
    listeners.forEach(fn => fn(globalPreferences));
    savePreferences(prefs);
  }, []);

  return {
    preferences,
    isLoading,
    updatePreference,
    setFullPreferences,
  };
}

// Export singleton getter for non-hook usage
export function getPreferences(): UIPreferences {
  return globalPreferences;
}

/**
 * Preload preferences from server
 * Call this BEFORE mounting components that use preferences (e.g., after PIN auth)
 * Returns a promise that resolves when preferences are loaded
 */
export async function preloadPreferences(): Promise<UIPreferences> {
  // If already initialized, return current preferences
  if (globalInitialized && !globalLoading) {
    console.log('[PREFERENCES] Already loaded, returning cached preferences');
    return globalPreferences;
  }

  // Wait for initialization to complete
  await initializePreferences();
  return globalPreferences;
}

export function updatePreferenceDirect<K extends keyof UIPreferences>(
  key: K,
  value: Partial<UIPreferences[K]>
): void {
  const current = globalPreferences[key] || {};
  const updated = typeof value === 'object' && value !== null
    ? { ...current, ...value }
    : value;

  globalPreferences = {
    ...globalPreferences,
    [key]: updated,
  };

  listeners.forEach(fn => fn(globalPreferences));
  savePreferences({ [key]: updated } as Partial<UIPreferences>);
}
